import { popularList } from "@/constants/apiConstantData";
import { useProfile } from "@/context/useProfileProps";
import { getInitials, monthData, yearData } from "@/helpers/helper";
import { useIsOtherContained, useMasterData } from "@/hooks";
import { nameRegex } from "@/utils/regex";
import { Input, Label } from "@monsterindia/global-ui-components";
import { PillController } from "@monsterindia/l1-apop";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";

import { SingleDropDownController } from "@/components/common/dropdownController";

import { useUpdatePersonal } from "../../../personalDetails/query";
import { BasicProps, DropdownOptionType } from "../../types";
import UploadAvatar from "../uploadAvatar";

const BasicFields = ({
  personalData,
  closeEdit,
  formRef,
  handleLoading,
  workAuthChangeRequired = false,
}: BasicProps) => {
  const [updateOnce, setUpdateOnce] = useState(false);
  const [genderList, setGenderList] = useState(popularList().gender ?? []);
  const { masterData, getMasterData } = useMasterData();
  const { mutate } = useUpdatePersonal();
  const {
    showToast,
    handleEventTracking,
    translation: { personal, basic, errorMessages },
  } = useProfile();

  const additionalKey = { id: "0", text: basic.iDontHaveAuth };

  const defaultValues =
    localStorage.getItem("basicFormData") &&
    localStorage.getItem("basicFormData") !== "{}"
      ? JSON.parse(localStorage.getItem("basicFormData") || "{}")
      : getDefaultValue(personalData, additionalKey, workAuthChangeRequired);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
    register,
  } = useForm<FieldValues>({ defaultValues });

  const getValues = () => control._formValues;
  const fullName = useWatch({ control, name: "fullName" });
  const gender = useWatch({ control, name: "gender" });
  const years = useWatch({ control, name: "years" });
  const months = useWatch({ control, name: "months" });
  const location = useWatch({ control, name: "location" });
  const cityName = useWatch({ control, name: "cityName" });
  const nationality = useWatch({ control, name: "nationality" });
  const workVisaType = useWatch({ control, name: "workVisaType" });
  const isOtherContained = useIsOtherContained(location);

  // Memoize location and nationality by their content (id)
  const memoizedLocation = useMemo(
    () => location,
    [
      location?.id,
      location?.extraInfo?.countryIsoCode,
      location?.extraInfo?.countryUuId,
    ],
  );

  const memoizedNationality = useMemo(
    () => nationality,
    [nationality?.id, nationality?.extraInfo?.countryUuId],
  );

  // Add state to track when fields are being directly updated
  const [isDirectUpdate, setIsDirectUpdate] = useState({
    location: false,
    nationality: false,
    workVisaType: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const formData = getValues();
    localStorage.setItem("basicFormData", JSON.stringify(formData));
  }, [
    fullName,
    years,
    months,
    cityName,
    workVisaType,
    location,
    nationality,
    gender,
    isSubmitting,
  ]);

  useEffect(() => {
    // Load master data for visa types
    getMasterData(
      "VISA_TYPE",
      "",
      "",
      memoizedLocation?.extraInfo?.countryIsoCode,
    );

    // Skip resetting during form submission
    if (isSubmitting) {
      return;
    }

    // Only reset workVisaType if location/nationality was changed directly
    // and not as a side effect of changing workVisaType
    if (updateOnce && !isDirectUpdate.workVisaType) {
      setValue("workVisaType", null);
    } else if (isDirectUpdate.workVisaType) {
      // Reset the flag after handling
      setIsDirectUpdate((prev) => ({ ...prev, workVisaType: false }));
    }

    // Reset location/nationality flags after handling
    if (isDirectUpdate.location || isDirectUpdate.nationality) {
      setIsDirectUpdate((prev) => ({
        ...prev,
        location: false,
        nationality: false,
      }));
    }
  }, [memoizedLocation, memoizedNationality]);

  useEffect(() => {
    setUpdateOnce(true);
  }, []);

  useEffect(() => {
    if (defaultValues?.gender && Object.keys(defaultValues?.gender).length) {
      const updatedList = genderList.map((item) => ({
        ...item,
        selected: item.id === defaultValues?.gender?.id,
      }));
      setGenderList(updatedList);
    }
  }, []);

  const onSubmit = (data: FieldValues) => {
    setIsSubmitting(true);
    handleLoading({ isSaveLoading: true });
    const payload = getPayload(data, isOtherContained);
    mutate(
      { data: payload, action: "edit" },
      {
        onSuccess: () => {
          handleLoading({ isSaveLoading: false });
          showToast({
            show: true,
            message: "Profile details updated successfully",
            type: "success",
          });
          let urlParams = new URLSearchParams(window.location.search);
          const isApp = urlParams.get("app") === "true";
          handleEventTracking({
            eventType: "eventPush",
            eventName: "ProfilePage_Section_Saved",
            eventProps: {
              Basic_Details_Saved: JSON.stringify(payload),
              Source: isApp ? "app" : "web",
            },
          });
          closeEdit("save");
          setIsSubmitting(false);
        },
        onError: (err) => {
          handleLoading({ isSaveLoading: false });
          showToast({
            show: true,
            message:
              err instanceof Error
                ? err.message
                : errorMessages.somethingWentWrong,
            type: "error",
          });
          setIsSubmitting(false);
        },
      },
    );
  };

  const handleSelect = (name: string, value: DropdownOptionType) => {
    clearErrors(name);

    // Set the appropriate flag based on which field is being updated
    if (
      name === "workVisaType" ||
      name === "location" ||
      name === "nationality"
    ) {
      setIsDirectUpdate((prev) => ({ ...prev, [name]: true }));
    }

    setValue(name, value);
  };

  const validations = {
    required: { value: true, message: errorMessages.fieldRequired },
    pattern: { value: nameRegex, message: basic.notValidName },
  };

  return (
    <form
      className="flex h-full flex-col gap-6"
      ref={formRef}
      onSubmit={handleSubmit(onSubmit)}
      id="basicFieldsForm"
    >
      <UploadAvatar
        avatar={personalData?.avatar ?? null}
        nameInitial={getInitials(personalData?.fullName ?? "")}
      />

      <Label labelText={basic.fullName} isRequired={true} className="text-sm">
        <Input
          id="fullName"
          maxLength={30}
          placeholder={basic.fullNamePlaceholder}
          errorText={errors.fullName?.message}
          {...register("fullName", validations)}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            const trimmedValue = e.target.value.trim();
            setValue("fullName", trimmedValue);
          }}
        />
      </Label>

      <Label labelText={basic.gender} isRequired={false} className="text-sm">
        <PillController
          name="gender"
          size="medium"
          selectType="single"
          control={control}
          options={genderList}
          isRequired={false}
          onSelect={(value: any) => handleSelect("gender", value[0])}
          errorMessage={""}
        />
      </Label>

      <Label labelText={basic.experience} isRequired={true} className="text-sm">
        <div className="flex gap-3">
          <SingleDropDownController
            name="years"
            control={control}
            label={basic.years}
            placeholder={basic.years}
            options={yearData}
            onSelect={(value) => handleSelect("years", value)}
            isLabelHidden={true}
            isRequired={true}
            errorMessage={errors.years ? errorMessages.fieldRequired : ""}
          />

          <SingleDropDownController
            name="months"
            control={control}
            label={basic.months}
            placeholder={basic.months}
            options={monthData}
            onSelect={(value) => handleSelect("months", value)}
            isLabelHidden={true}
            isRequired={true}
            errorMessage={errors.months ? errorMessages.fieldRequired : ""}
          />
        </div>
      </Label>

      <SingleDropDownController
        name="location"
        type="LOCATION"
        isRequired={true}
        control={control}
        isSearchable={true}
        label={basic.location}
        placeholder={basic.locationPlaceholder}
        onSelect={(value) => handleSelect("location", value)}
        errorMessage={errors.location ? errorMessages.fieldRequired : ""}
      />

      {isOtherContained && (
        <Label labelText={basic.cityName} isRequired={true} className="text-sm">
          <Input
            id="cityName"
            placeholder={basic.cityNamePlaceholder}
            errorText={errors.cityName?.message}
            {...register("cityName", validations)}
          />
        </Label>
      )}

      <SingleDropDownController
        name="nationality"
        type="NATIONALITY"
        isRequired={true}
        control={control}
        isSearchable={true}
        label={personal.nationality}
        placeholder={personal.nationalityPlaceholder}
        onSelect={(value) => handleSelect("nationality", value)}
        errorMessage={errors.nationality ? errorMessages.fieldRequired : ""}
      />

      {location?.extraInfo?.countryUuId &&
        nationality?.extraInfo?.countryUuId &&
        location?.extraInfo?.countryUuId !==
          nationality?.extraInfo?.countryUuId && (
          <SingleDropDownController
            name="workVisaType"
            isRequired={true}
            control={control}
            options={masterData ?? []}
            label={`${basic.workAuthorization} ${location?.extraInfo?.countryName ?? "Location"}`}
            placeholder={basic.workAuthPlaceholder}
            onSelect={(value) => handleSelect("workVisaType", value)}
            errorMessage={
              errors.workVisaType ? errorMessages.fieldRequired : ""
            }
          />
        )}
    </form>
  );
};

const getPayload = (data: FieldValues, isOtherContained: boolean) => ({
  additionalPersonalDetail: {
    ...(data?.gender?.id && { gender: { id: data?.gender?.id } }),
    currentLocation: {
      id: data?.location?.id,
      ...(isOtherContained && { otherText: data?.cityName }),
    },
    experience: {
      months: data.months.id,
      years: data.years.id,
    },
    ...(data?.workVisaType?.text && {
      ...(data?.workVisaType?.id !== "0" && {
        workVisaType: { id: data?.workVisaType?.id },
      }),
      workVisaTypeUpdated: true,
    }),
  },
  fullName: data?.fullName,
  nationality: { id: data?.nationality?.id },
});

const getDefaultValue = (
  data: any,
  authDefaultVal?: { id: string; text: string },
  workAuthChangeRequired?: boolean,
) => {
  let {
    fullName = "",
    nationality = null,
    additionalPersonalDetail: {
      currentLocation: location = null,
      gender: gender = null,
      experience: { months, years } = {},
      workVisaType = null,
    } = {},
  } = data || {};

  // work auth for Singapore country
  const isCountrySingapore =
    location &&
    location?.extraInfo?.countryUuId &&
    location?.extraInfo?.countryUuId === "d0fbac9b-a9bb-47de-ace2-72dadcc086f4";

  if (!isCountrySingapore && workVisaType === null) {
    workVisaType = authDefaultVal;
  }

  const result: any = {
    fullName: fullName.slice(0, 30),
    gender,
    location,
    cityName: location?.otherText || "",
    nationality,
    workVisaType,
  };

  if (typeof years !== "undefined") {
    result.years = {
      id: years,
      text: `${years} ${years == "1" || years == "0" ? "year" : "years"}`,
    };
  }

  if (typeof months !== "undefined") {
    result.months = {
      id: months,
      text: `${months} ${months == "1" || months == "0" ? "month" : "months"}`,
    };
  }

  return result;
};

export default BasicFields;
